import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
  Toolbar,
  Hidden,
  List,
  ListItem,
  ListItemIcon,
  Popover,
  Typography,
  IconButton,
  Button,
  Avatar
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import MenuIcon from '@material-ui/icons/Menu';
import { Image, DarkModeToggler } from 'components/atoms';
import logoIcon from 'assets/images/crowded_logo_h.png';
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from "react-router-dom";
import { setProfile } from 'redux/actions/AuthActions';
import { callGetApi } from 'utils/networking';

const useStyles = makeStyles(theme => ({
  flexGrow: {
    flexGrow: 1,
  },
  navigationContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  toolbar: {
    zIndex: 999,
    maxWidth: theme.layout.contentWidth,
    width: '100%',
    margin: '0 auto',
    padding: theme.spacing(0, 2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(0, 8),
    },
  },
  navLink: {
    '&:hover': {
      color: theme.palette.primary.dark,
    },
  },
  listItem: {
    cursor: 'pointer',
    '&:hover > .menu-item, &:hover svg': {
      color: theme.palette.primary.dark,
    },
    '&.menu-item--no-dropdown': {
      paddingRight: 0,
    },
  },
  listItemActive: {
    '&> .menu-item': {
      color: theme.palette.primary.dark,
    },
  },
  listItemText: {
    flex: '0 0 auto',
    marginRight: theme.spacing(2),
    whiteSpace: 'nowrap',
  },
  listItemButton: {
    whiteSpace: 'nowrap',
  },
  listItemIcon: {
    minWidth: 'auto',
  },
  popover: {
    padding: theme.spacing(4),
    border: theme.spacing(2),
    boxShadow: '0 0.5rem 2rem 2px rgba(116, 123, 144, 0.09)',
    minWidth: 350,
    marginTop: theme.spacing(2),
  },
  iconButton: {
    marginLeft: theme.spacing(2),
    padding: 0,
    '&:hover': {
      background: 'transparent',
    },
  },
  expandOpen: {
    transform: 'rotate(180deg)',
    color: theme.palette.primary.dark,
  },
  logoContainer: {
    width: 100,
    height: 28,
    [theme.breakpoints.up('md')]: {
      width: 100,
      height: 28,
    },
  },
  logoImage: {
    width: '160px',
    height: '35px',
  },
  menu: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  menuItem: {
    marginRight: theme.spacing(5),
    '&:last-child': {
      marginRight: 0,
    },
  },
  menuGroupItem: {
    paddingTop: 0,
  },
  menuGroupTitle: {
    textTransform: 'uppercase',
  },
  avatarContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatar: {
    marginRight: theme.spacing(1),
  },
}));

const Topbar = ({ themeMode, themeToggler, onSidebarOpen, pages, className, ...rest }) => {
  const classes = useStyles();
  const profileData = useSelector((state) => state.auth.profileData)
  const dispatch = useDispatch();
  const history = useHistory();
  //console.log('profileData', profileData)    

  const [anchorEl, setAnchorEl] = useState(null);
  const [openedPopoverId, setOpenedPopoverId] = useState(null);

  const handleClick = (event, popoverId) => {
    setAnchorEl(event.target);
    setOpenedPopoverId(popoverId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpenedPopoverId(null);
  };

  const handleLogout = () => {
    dispatch(setProfile(null));
    localStorage.setItem('token', '')
    handleClose();
    history.replace('/');
  }

  useEffect(() => {
    callGetApi('user/verify_token', dispatch, (data) => {
      if (data && data.id) {
        dispatch(setProfile(data));
      }
    }, null, false)
  }, [])

  const account = pages.profile;

  const MenuGroup = props => {
    const { item } = props;
    return (
      <List disablePadding>
        <ListItem disableGutters>
          <Typography
            variant="body2"
            color="primary"
            className={classes.menuGroupTitle}
          >
            {item.groupTitle}
          </Typography>
        </ListItem>
        {item.pages.map((page, i) => (
          <ListItem disableGutters key={i} className={classes.menuGroupItem}>
            {page.title === 'Logout' ? (
              <Typography
                variant="body1"
                component={'a'}
                href="/"
                className={clsx(classes.navLink, 'submenu-item')}
                color="textSecondary"
                onClick={e => {
                  e.stopPropagation()
                  handleLogout()
                }}
              >
                {page.title}
              </Typography>
            ) : (
              <Typography
                variant="body1"
                component={'a'}
                href={page.href}
                className={clsx(classes.navLink, 'submenu-item')}
                color="textSecondary"
                onClick={handleClose}
              >
                {page.title}
              </Typography>
            )}
          </ListItem>
        ))}
      </List>
    );
  };

  const AccountPages = () => {
    const { settings, security } = account.children;
    return (
      <div className={classes.menu}>
        <div className={classes.menuItem}>
          <MenuGroup item={settings} />
        </div>
        <div className={classes.menuItem}>
          <MenuGroup item={security} />
        </div>
      </div>
    );
  };

  const renderPages = id => {
    if (id === 'account') {
      return <AccountPages />;
    }
  };

  return (
    <Toolbar disableGutters className={classes.toolbar} {...rest}>
      <div className={classes.logoContainer}>
        <a href="/" title="thefront">
          <Image
            className={classes.logoImage}
            src={themeMode === 'light' ? logoIcon : logoIcon}
            alt="thefront"
            lazy={false}
          />
        </a>
      </div>
      <div className={classes.flexGrow} />
      <Hidden smDown>
        <List disablePadding className={classes.navigationContainer}>
          <ListItem className={clsx(classes.listItem, 'menu-item--no-dropdown')}>
            <Button
              component="a"
              href="/"
            >
              Home
            </Button>
          </ListItem>
          <ListItem className={clsx(classes.listItem, 'menu-item--no-dropdown')}>
            <Button
              component="a"
              href="/discover"
            >
              Discover
            </Button>
          </ListItem>
          <ListItem className={clsx(classes.listItem, 'menu-item--no-dropdown')}>
            <Button
              component="a"
              href="/publication"
            >
              Publication
            </Button>
          </ListItem>
          <ListItem className={clsx(classes.listItem, 'menu-item--no-dropdown')}>
            <Button
              component="a"
              href="/community"
            >
              Community
            </Button>
          </ListItem>
          <ListItem className={clsx(classes.listItem, 'menu-item--no-dropdown')}>
            <Button
              component="a"
              href="/artists"
            >
              Artists
            </Button>
          </ListItem>
          <ListItem className={clsx(classes.listItem, 'menu-item--no-dropdown')}>
            <Button
              component="a"
              href="/podcasts"
            >
              Podcasts
            </Button>
          </ListItem>
          <ListItem className={clsx(classes.listItem, 'menu-item--no-dropdown')}>
            <Button
              component="a"
              href="/members"
            >
              Members
            </Button>
          </ListItem>
          <ListItem className={clsx(classes.listItem, 'menu-item--no-dropdown')}>
            <Button
              component="a"
              href="/voting"
            >
              Vote
            </Button>
          </ListItem>
          <ListItem className={clsx(classes.listItem, 'menu-item--no-dropdown')}>
            <Button
              component="a"
              href="https://www.crowdedstreaming.com/shop"
            >
              Shop
            </Button>
          </ListItem>
          {
            (profileData && profileData.id) ? (<div key="account">
              <ListItem
                aria-describedby="account"
                onClick={e => handleClick(e, "account")}
                className={clsx(
                  classes.listItem,
                  openedPopoverId === "account" ? classes.listItemActive : '',
                )}
              >
                <div className={classes.avatarContainer}>
                  <Avatar {...{ src: profileData.avatarURL }} className={classes.avatar} />
                  <Typography variant="body2" color="textPrimary" className={clsx(classes.listItemText, 'menu-item')}>
                    {profileData.username}
                  </Typography>
                </div>
                <ListItemIcon className={classes.listItemIcon}>
                  <ExpandMoreIcon
                    className={
                      openedPopoverId === "account" ? classes.expandOpen : ''
                    }
                    fontSize="small"
                  />
                </ListItemIcon>
              </ListItem>
              <Popover
                elevation={1}
                id="account"
                open={openedPopoverId === "account"}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                classes={{ paper: classes.popover }}
              >
                <div>{renderPages("account")}</div>
              </Popover>
            </div>) : (<ListItem className={clsx(classes.listItem, 'menu-item--no-dropdown')}>
              <Button
                component="a"
                href="/signin"
              >
                Login
              </Button>
            </ListItem>)
          }
        </List>
      </Hidden>
      <Hidden mdUp>
        {/* <DarkModeToggler themeMode={themeMode} onClick={() => themeToggler()} /> */}
        <IconButton
          className={classes.iconButton}
          onClick={onSidebarOpen}
          aria-label="Menu"
        >
          <MenuIcon />
        </IconButton>
      </Hidden>
    </Toolbar>
  );
};

Topbar.propTypes = {
  className: PropTypes.string,
  onSidebarOpen: PropTypes.func,
  pages: PropTypes.object.isRequired,
  themeToggler: PropTypes.func.isRequired,
  themeMode: PropTypes.string.isRequired,
};

export default Topbar;
